// Card 50, the gut marks (R2637, R2723; ui-map-interface.md §17.3.50,
// v2-the-deep.md §13; mockup round 031 card 50 and round 115 cards 02 and
// 07).
//
// A GUT is a gap of one or two water cells between two rock cells. A pair
// of marks stands at each gut, one on the rock at each end, facing into
// the gap, so a reader sees the passage before a course is laid. The
// owner picked one set of round 115 for the fairway (card 70), the guts,
// the shipping lane (card 16) and the Fanal (R2723), and the chart
// reading picks the card. The modern reading draws card 02 Glow Channel: a
// lit post on each rock face and a faint glow in the gap, with no word.
// The historical reading draws card 07 Engraved Survey: a perch, a pole
// with a ball, on each rock, and the name "the Gut". The piece keeps its
// slug `cardinal_marks`, because a stored Chart theme names it.
//
// **WHY THE PIRATES.** A gut is water where a pirate group meets a fleet
// that cannot pass it wide (v2 §8.2), so v2 §13 gives the card to the NPC
// side. A gut is not the narrows of a fairway (§7.1), which card 70
// draws. The page writes the flag `pirates` into `ChartContext.npcWater`
// (§14.1 rule 5: the control reads the flag, the piece draws what it is
// handed), so a world with the flag off shows the chart of today.
//
// **ONE PAIR A GUT.** A long canal one cell wide is a gap at every cell.
// The walk takes the cells in cell order, and a gap nearer than
// `spacing_px` to a kept one is the same gut, so a canal takes one
// pair. At most `max_guts` pairs stand on a map.
//
// **COST.** Terrain never changes inside a world, so the pack is static and
// the walk runs once per hello: six neighbour reads a water cell (§10). A
// glow is one quad of the glow cell of the set (`render/fairway-cells.ts`).

import { BLOCK_RIDGE } from '../../../gen/balance';
import { MARKER_NEUTRAL } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { COLS, ROWS, cellCenterX, cellCenterY, neighborAt } from '../../../world';
import { GLOW_CELL_R, GLOW_KEY } from '../../../render/fairway-cells';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, faceOf, inkOf } from './shared';

/** One gut: the rock cells at its two ends, the centre of its gap and
 *  its width in cells. */
export interface Gut { a: number; b: number; x: number; y: number; width: 1 | 2 }

/**
 * The guts of a map, in cell order (R2637). A water cell `c` opens a gap
 * along an axis when the cell behind it is rock and the cell ahead (width 1)
 * or the one after (width 2) is rock too. The gap is a way through only
 * when water lies on both sides of the axis, so a pocket in the rock is no
 * gut. Off the grid is neither rock nor water.
 */
export function findGuts(
  blocked: Uint8Array, spacingPx: number, max: number, cols = COLS, rows = ROWS,
): Gut[] {
  const out: Gut[] = [];
  const n = Math.min(blocked.length, cols * rows);
  const rock = (c: number) => c >= 0 && blocked[c] === BLOCK_RIDGE;
  const water = (c: number) => c >= 0 && c < n && blocked[c] !== BLOCK_RIDGE;
  const side = (c: number, k: number) =>
    water(neighborAt(c, (k + 1) % 6, cols, rows)) || water(neighborAt(c, (k + 2) % 6, cols, rows));
  const other = (c: number, k: number) =>
    water(neighborAt(c, (k + 4) % 6, cols, rows)) || water(neighborAt(c, (k + 5) % 6, cols, rows));
  const spacing2 = spacingPx * spacingPx;
  for (let c = 0; c < n && out.length < max; c++) {
    if (!water(c)) continue;
    for (let k = 0; k < 3; k++) {
      const back = neighborAt(c, k + 3, cols, rows);
      if (!rock(back)) continue;
      const f1 = neighborAt(c, k, cols, rows);
      let end = -1, last = c, width: 1 | 2 = 1;
      if (rock(f1)) end = f1;
      else if (water(f1)) {
        const f2 = neighborAt(f1, k, cols, rows);
        if (rock(f2)) { end = f2; last = f1; width = 2; }
      }
      if (end < 0) continue;
      if (!(side(c, k) || side(last, k)) || !(other(c, k) || other(last, k))) continue;
      const x = (cellCenterX(c, cols) + cellCenterX(last, cols)) / 2;
      const y = (cellCenterY(c, cols) + cellCenterY(last, cols)) / 2;
      if (out.some((m) => (m.x - x) ** 2 + (m.y - y) ** 2 < spacing2)) continue;
      out.push({ a: back, b: end, x, y, width });
      break;
    }
  }
  return out;
}

/** A point `d` px from the centre of the rock cell `rock` toward the gap
 *  centre (gx, gy), and the unit axis from the rock into the gap. */
export function gutFace(rock: number, gx: number, gy: number, d: number): { x: number; y: number; ux: number; uy: number } {
  const rx = cellCenterX(rock), ry = cellCenterY(rock);
  const len = Math.hypot(gx - rx, gy - ry) || 1;
  const ux = (gx - rx) / len, uy = (gy - ry) / len;
  return { x: rx + ux * d, y: ry + uy * d, ux, uy };
}

export class CardinalMarksPiece implements Piece {
  readonly id = 'cardinal_marks' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The NPC side plays in the world running now: a film hides the marks. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'reading', 'zoomband', 'font'] as const;

  /** The guts of the last pack. The tests read them. */
  guts: Gut[] = [];
  /** The world played the NPC side at the last pack, for `outgrown`. */
  private on = false;

  visible(ctx: ChartContext): boolean {
    return ctx.npcWater && ctx.scene.blocked !== null;
  }

  rebuild(ctx: ChartContext): void {
    this.on = ctx.npcWater;
    const blocked = ctx.scene.blocked;
    const f = ctx.cfg.pieces.cardinal_marks.fig;
    this.guts = blocked ? findGuts(blocked, f.spacing_px, f.max_guts) : [];
  }

  /** The flag edge is one no reason names: one compare a frame. */
  outgrown(ctx: ChartContext): boolean {
    return ctx.npcWater !== this.on;
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const t = ctx.cfg.pieces.cardinal_marks;
    const pale = inkOf(t[ctx.reading].ink, CHART_INK.pale);
    if (ctx.reading === 'historical') {
      // Card 07: a perch on each rock, and the name under the gap.
      const r = t.historical, f = r.fig;
      const face = faceOf('historical');
      for (const g of this.guts) {
        for (const rock of [g.a, g.b]) {
          const p0 = gutFace(rock, g.x, g.y, f.perch_from_px), p1 = gutFace(rock, g.x, g.y, f.perch_to_px);
          sinks.segments.line(p0.x, p0.y, p1.x, p1.y, r.width, pale, r.alpha);
          const ball = gutFace(rock, g.x, g.y, f.ball_at_px);
          sinks.circles.disc(ball.x, ball.y, f.ball_px, pale, f.ball_alpha);
        }
        ctx.glyphs.word(sinks.quads, S.gut_historical, g.x, g.y + f.label_dy, face, t.text.main, pale,
          f.label_alpha, { anchor: 'centre' });
      }
      return;
    }
    // Card 02: the gap glows, and a lit post stands on each rock face.
    const r = t.modern, f = r.fig;
    const glow = ctx.glyphs.mark(GLOW_KEY);
    for (const g of this.guts) {
      sinks.quads.sprite(g.x, g.y, 0, glow, pale, f.gap_glow_alpha, (f.gap_glow_px + f.gap_glow_cell_px * g.width) / GLOW_CELL_R);
      for (const rock of [g.a, g.b]) {
        const p = gutFace(rock, g.x, g.y, f.post_face_px);
        sinks.quads.sprite(p.x, p.y, 0, glow, MARKER_NEUTRAL, f.post_glow_alpha, f.post_glow_px / GLOW_CELL_R);
        const h = f.post_half_px;
        sinks.segments.line(p.x + p.uy * h, p.y - p.ux * h, p.x - p.uy * h, p.y + p.ux * h, r.width, MARKER_NEUTRAL, r.alpha);
      }
    }
  }
}

/** The one instance the registry holds. */
export const cardinalMarks = new CardinalMarksPiece();
